import {
  ifElse,
  prop,
  includes,
  pipe,
  always,
  allPass,
  filter,
  pathEq,
  gte,
  modulo,
  __,
  equals,
  propOr,
  isEmpty,
  isNil,
  memoizeWith,
  identity
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { formatCurrency } from '../../../utils/currency'
import {
  getFirstMonthPromotionsOfItemChargedOnlyFirstMonth,
  isTOSPartnershipOrTOSBreachFlow,
  getCacheKey,
  hasSmartHomeSecurity,
  isTOS
} from '../../../utils/cart'
import { buildHSNonRecurringDiscounts, getDiscountDisplayName } from '../../../utils/discount'
import { buildHSCreditList } from '../../../utils/display'
import {
  formatHSEppDiscount,
  getHSEppDiscount,
  getServiceWithoutSpecifiedDiscounts
} from '../../../utils/homeSolutions'

const { getTopLevelItems, getMonthlyTotalPrice, getMonthlyDiscounts, getDisplayName } = utils.cart

export const getStandaloneAddons = memoizeWith(
  getCacheKey('Addon'),
  pipe(getTopLevelItems, filter(pipe(prop('tags'), (tags) => tags?.includes('addon'))))
)

export const getAddonTotalMonthlyPrice = pipe(
  getMonthlyTotalPrice,
  ifElse(pathEq(['duration'], 1), always({}), identity)
)

export const isAPikAddon = pipe(
  propOr([], 'tags'),
  allPass([includes('tv'), includes('addon'), includes('tvx')])
)

export const isATOSAddon = pipe(
  propOr([], 'tags'),
  allPass([includes('tos-standalone'), includes('addon')])
)

export const isSHSWithTOS = (cart, addon) => hasSmartHomeSecurity(cart) && isTOS(addon)

export const isIncludedAddon = (cart, addon, featureFlags) =>
  featureFlags.includedAddon && isSHSWithTOS(cart, addon)

export const isAnInternet = pipe(
  propOr([], 'tags'),
  allPass([includes('internet'), includes('addon')])
)

export function setAddonUrl({ service, pikUrl, internetUrl, isBusinessCart }) {
  const addonPikUrl = pikUrl || '/my-account/manage-services/home/pik/packages'
  const addonTOSUrl = '/shop/home-services/bundle/build-your-own'
  const addonInternetUrl = internetUrl || '/my-account/internet/select-plan/plan'
  const businessUrl = '/shop/business-small/customize?checkout=express'

  if (isBusinessCart) return businessUrl
  if (isAPikAddon(service)) return addonPikUrl
  if (isAnInternet(service)) return addonInternetUrl
  if (isATOSAddon(service)) return addonTOSUrl

  return ''
}

export const setUrlByAuthenticationAndService = ({
  isAuthenticated,
  service,
  cart,
  featureFlags,
  isBusinessCart,
  addonIncluded
}) => {
  // when add-on is included with an offer, change url is not applicable
  if (addonIncluded) return ''

  // When TOS partnership or breach flow, change url is not applicable
  if (isTOSPartnershipOrTOSBreachFlow(featureFlags, cart)) return ''

  // NOT authenticated
  if (!isAuthenticated) {
    return setAddonUrl({
      service,
      pikUrl: '/shop/home-services/customize',
      internetUrl: '/shop/home-services/customize',
      isBusinessCart
    })
  }

  // authenticated
  return setAddonUrl({ service })
}

const getMonthlyTermDiscounts = (monthlyDiscounts) => {
  const nonInfiniteDiscount = gte(__, 1)
  const isDivisibleByYear = pipe(modulo(__, 12), equals(0))

  const isDiscountTerm = pipe(prop('duration'), allPass([nonInfiniteDiscount, isDivisibleByYear]))

  const termDiscounts = monthlyDiscounts?.filter(isDiscountTerm)

  return termDiscounts
}

export const buildRecurringDiscountList = (addon, lang, translate, isAffiliates) => {
  // Business rule: EPP discount must be displayed as a separate line item, and is displayed last
  const eppDiscount = getHSEppDiscount(addon)
  const addonWithoutEppDiscount = getServiceWithoutSpecifiedDiscounts(addon, [eppDiscount])

  const monthlyDiscounts = getMonthlyDiscounts(addonWithoutEppDiscount)

  const termDiscounts = getMonthlyTermDiscounts(monthlyDiscounts)

  const hasTermDiscounts = termDiscounts?.length > 0

  const filteredMonthlyDiscounts = hasTermDiscounts
    ? termDiscounts
    : monthlyDiscounts.filter(pipe(prop('duration'), (duration) => duration === 0))

  const formattedMonthlyDiscounts = filteredMonthlyDiscounts?.map((discount, idx) => ({
    name: getDiscountDisplayName(discount),
    price: `-${formatCurrency(discount.amount, lang)}/mo.`,
    id: `${addon.id}-discount-${idx}`,
    prefixIcon: 'DEALS'
  }))

  if (eppDiscount) formattedMonthlyDiscounts?.push(formatHSEppDiscount(eppDiscount, translate, lang, isAffiliates))

  return formattedMonthlyDiscounts
}

export const buildFirstMonthPromotionsForFirstMonthPaymentOnlyItemList = (
  addon,
  isFirstMonthPaymentOnly,
  lang
) => {
  if (!isFirstMonthPaymentOnly) return []

  const firstMonthPromotions = getFirstMonthPromotionsOfItemChargedOnlyFirstMonth(addon)

  return firstMonthPromotions?.map((promotion) => ({
    id: promotion.id,
    name: getDisplayName(promotion),
    price: `-${formatCurrency(promotion.amount, lang)}`,
    prefixIcon: promotion.isCouponCode ? 'PIGGYBANK' : 'DEALS'
  }))
}

export const buildDiscountsAndCreditsList = (addon, cart, lang) => {
  const discounts = buildHSNonRecurringDiscounts([addon], lang)
  const credits = buildHSCreditList('addon', cart, lang)
  return [...discounts, ...credits]
}

/**
 * Gets Contentful legal footnote slug from locales.
 * The result to be passed as an argument to the translate hook.
 * @param {object} Item - Top-level item from cart
 * @return {string} The key used to parse the locales
 */
export const getFootNoteKey = ifElse(
  pipe(prop('tags'), includes('financing')),
  always('easyPaymentcancellationFeeSlug'),
  always(undefined)
)

export const shouldRender = (cart) => {
  const addons = getStandaloneAddons(cart)
  return !isNil(addons) && !isEmpty(addons)
}
